/**
 * BeforeAfterSlider Component
 *
 * Interactive before/after comparison slider for case study pages.
 * Uses react-compare-slider for lightweight, accessible slider functionality.
 *
 * Following CLAUDE.md:
 * - Client component for interactivity
 * - Styled with Tailwind CSS utilities
 * - Brand-agnostic (uses semantic design tokens)
 * - Keyboard accessible (arrow keys to move slider)
 * - Touch-friendly for mobile
 */

"use client";

import {
	ReactCompareSlider,
	ReactCompareSliderImage,
} from "react-compare-slider";
import { cn } from "@/lib/utils";
import type { Image as ImageType } from "@/lib/validation/common";

export interface BeforeAfterSliderProps {
	before: ImageType;
	after: ImageType;
	caption?: string;
	className?: string;
}

/**
 * Custom handle component for the slider divider
 * Uses brand primary (yellow) color for prominence and visual consistency
 */
function SliderHandle() {
	return (
		<div className="relative flex h-full w-1 items-center justify-center">
			{/* Vertical line - brand primary color */}
			<div className="absolute h-full w-1.5 bg-primary shadow-lg" />

			{/* Circular button - brand primary with dark text */}
			<div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-xl ring-2 ring-primary-foreground/20 transition-all duration-200 hover:scale-110 hover:bg-primary-hover">
				{/* Left arrow - larger and bolder */}
				<svg
					className="absolute left-2 h-5 w-5 text-primary-foreground"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
				>
					<title>Drag left</title>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={3}
						d="M15 19l-7-7 7-7"
					/>
				</svg>

				{/* Right arrow - larger and bolder */}
				<svg
					className="absolute right-2 h-5 w-5 text-primary-foreground"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
				>
					<title>Drag right</title>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={3}
						d="M9 5l7 7-7 7"
					/>
				</svg>
			</div>
		</div>
	);
}

/**
 * Label component for Before/After text
 */
function Label({
	children,
	position,
}: {
	children: React.ReactNode;
	position: "left" | "right";
}) {
	return (
		<div
			className={cn(
				"absolute top-4 z-10 rounded bg-black/70 px-3 py-1 text-sm font-semibold text-white",
				position === "left" ? "left-4" : "right-4",
			)}
		>
			{children}
		</div>
	);
}

export function BeforeAfterSlider({
	before,
	after,
	caption,
	className,
}: BeforeAfterSliderProps) {
	return (
		<figure className={cn("group relative", className)}>
			{/* Before label */}
			<Label position="left">Before</Label>

			{/* After label */}
			<Label position="right">After</Label>

			{/* Compare slider */}
			<ReactCompareSlider
				handle={<SliderHandle />}
				itemOne={
					<ReactCompareSliderImage
						src={before.url}
						alt={before.alternativeText}
						style={{ objectFit: "cover" }}
					/>
				}
				itemTwo={
					<ReactCompareSliderImage
						src={after.url}
						alt={after.alternativeText}
						style={{ objectFit: "cover" }}
					/>
				}
				className="aspect-video overflow-hidden rounded-lg"
				position={50} // Start at 50% (middle)
				changePositionOnHover={false} // Require drag interaction
				onlyHandleDraggable // Only the handle drags, so touching the image still scrolls the page (mobile)
				// Override the library's `touch-action: none` so vertical swipes scroll the
				// page while horizontal drags still move the handle.
				style={{ touchAction: "pan-y" }}
			/>

			{/* Optional caption */}
			{caption && (
				<figcaption className="mt-3 text-center text-sm text-muted-foreground">
					{caption}
				</figcaption>
			)}
		</figure>
	);
}
